import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  INTERVALS,
  intervalLabel,
  type AdminOrgSummary,
  type PlanDetail,
  type PlanInterval,
  type PlanOrgRef,
  type PlanWriteRequest,
} from "@currencycore/shared";
import {
  addPlanOrg,
  ApiRequestError,
  copyPlan,
  deletePlan,
  getPlan,
  listOrgs,
  removePlanOrg,
  updatePlan,
} from "../lib/api.ts";
import { useConfirm } from "../components/useConfirm.tsx";
import { toast } from "../components/Toast.tsx";
import { errMsg, useDebounced } from "../lib/ui.ts";

const numOrEmpty = (n: number | null): string => (n == null ? "" : String(n));
const centsToDollars = (c: number | undefined): string => (c == null ? "" : String(c / 100));

// Pricing suggestion — a power-law fit to the default catalog
// (Pro: 250k req → $29/mo, Scale: 2M req → $99/mo) so larger plans get a
// gentle volume discount. Purely a hint; the admin sets the real price.
const RATE_K = 0.01894;
const RATE_P = 0.5906;
function niceRound(n: number): number {
  if (n >= 500) return Math.round(n / 50) * 50;
  if (n >= 100) return Math.round(n / 10) * 10;
  if (n >= 20) return Math.round(n / 5) * 5;
  return Math.max(1, Math.round(n));
}
function suggestMonthlyUsd(requests: number): number {
  if (!requests || requests <= 0) return 0;
  return niceRound(RATE_K * Math.pow(requests, RATE_P));
}
/** Per-interval suggestion from the monthly figure, with the usual discounts. */
function suggestedIntervalPrices(monthlyUsd: number): Record<PlanInterval, number> {
  return {
    weekly: niceRound(monthlyUsd / 4),
    monthly: monthlyUsd,
    quarterly: niceRound(monthlyUsd * 3 * 0.95),
    half_yearly: niceRound(monthlyUsd * 6 * 0.9),
    annual: niceRound(monthlyUsd * 10),
  };
}

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanDetail | null>(null);
  const [name, setName] = useState("");
  const [monthly, setMonthly] = useState("");
  const [rate, setRate] = useState("");
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [overagePrice, setOveragePrice] = useState("");
  const [overageUnit, setOverageUnit] = useState("");
  const [dodoIds, setDodoIds] = useState<Record<string, string>>({});
  const [archived, setArchived] = useState(false);
  const [historyApiEnabled, setHistoryApiEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  // Default (standard) plans load read-only; the admin flips this on with "Edit".
  // Custom plans are always editable, so this flag is ignored for them.
  const [editing, setEditing] = useState(false);
  const { ask, dialog } = useConfirm();

  function hydrate(p: PlanDetail) {
    setPlan(p);
    setName(p.name);
    setMonthly(numOrEmpty(p.monthlyRequests));
    setRate(numOrEmpty(p.rateLimit));
    setArchived(p.archived);
    setHistoryApiEnabled(p.historyApiEnabled);
    setOveragePrice(p.overageCents != null ? String(p.overageCents / 100) : "");
    setOverageUnit(p.overageUnit != null ? String(p.overageUnit) : "");
    const px: Record<string, string> = {};
    for (const i of INTERVALS) px[i] = centsToDollars(p.prices[i]);
    setPrices(px);
    const dids: Record<string, string> = {};
    for (const i of INTERVALS) dids[i] = p.dodoProductIds[i] ?? "";
    setDodoIds(dids);
  }

  useEffect(() => {
    if (!id) return;
    getPlan(id).then(hydrate).catch((e) => toast(errMsg(e, "Failed to load plan.")));
  }, [id]);

  if (!plan) {
    return <div className="page-loading"><span className="spinner spinner-lg" /></div>;
  }

  const isStandard = plan.kind === "standard";
  const readOnly = isStandard && !editing;
  const monthlyReq = monthly.trim() === "" ? 0 : Number(monthly);
  const suggested = monthlyReq > 0 ? suggestMonthlyUsd(monthlyReq) : 0;

  function applySuggested() {
    const px = suggestedIntervalPrices(suggested);
    setPrices(Object.fromEntries(INTERVALS.map((i) => [i, String(px[i])])) as Record<string, string>);
    toast("Filled prices with the suggestion. Adjust as needed.", true);
  }

  function buildBody(): PlanWriteRequest {
    const px: Record<string, number> = {};
    for (const i of INTERVALS) {
      const v = prices[i]?.trim();
      if (v !== undefined && v !== "") {
        const cents = Math.round(Number(v) * 100);
        if (Number.isFinite(cents) && cents >= 0) px[i] = cents;
      }
    }
    const dodo: Record<string, string> = {};
    for (const i of INTERVALS) {
      const v = dodoIds[i]?.trim();
      if (v) dodo[i] = v;
    }
    return {
      name: name.trim(),
      monthlyRequests: monthly.trim() === "" ? null : Math.max(0, Math.floor(Number(monthly))),
      rateLimit: rate.trim() === "" ? null : Math.max(1, Math.floor(Number(rate))),
      prices: px,
      overageCents: overagePrice.trim() === "" ? null : Math.round(Number(overagePrice) * 100),
      overageUnit: overageUnit.trim() === "" ? null : Math.max(1, Math.floor(Number(overageUnit))),
      dodoProductIds: dodo,
      archived,
      historyApiEnabled,
    };
  }

  async function save() {
    setSaving(true);
    try {
      const p = await updatePlan(id!, buildBody());
      hydrate({ ...p, orgCount: plan!.orgCount, orgs: plan!.orgs });
      setEditing(false); // collapse back to read-only (no effect for custom plans)
      toast("Plan saved.", true);
    } catch (e) {
      toast(errMsg(e, "Could not save the plan."));
    } finally {
      setSaving(false);
    }
  }

  /** Discard in-progress edits on a default plan and re-lock the form. */
  function cancelEdit() {
    if (plan) hydrate(plan);
    setEditing(false);
  }

  async function copy() {
    try {
      const p = await copyPlan(id!);
      toast("Plan copied.", true);
      navigate(`/plans/${p.id}`);
    } catch (e) {
      toast(errMsg(e, "Could not copy the plan."));
    }
  }

  function confirmDelete() {
    ask({
      title: "Delete plan?",
      message: <>Permanently delete <strong>{plan!.name}</strong>?</>,
      confirmLabel: "Delete",
      danger: true,
      run: async () => {
        try {
          await deletePlan(id!);
          toast("Plan deleted.", true);
          navigate("/plans");
        } catch (e) {
          toast(e instanceof ApiRequestError ? e.body.error.message : errMsg(e, "Could not delete."));
        }
      },
    });
  }

  function mergeOrgs(d: PlanDetail) {
    setPlan((prev) => (prev ? { ...prev, orgs: d.orgs, orgCount: d.orgCount } : prev));
  }

  function confirmRemove(o: PlanOrgRef) {
    ask({
      title: "Remove organization?",
      message: (
        <>
          Remove <strong>{o.name}</strong> from this plan's list? It will no longer be available to them.
        </>
      ),
      confirmLabel: "Remove",
      danger: true,
      run: async () => {
        try {
          mergeOrgs(await removePlanOrg(plan!.id, o.id));
          toast("Organization removed.", true);
        } catch (e) {
          toast(e instanceof ApiRequestError ? e.body.error.message : errMsg(e, "Could not remove."));
        }
      },
    });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/plans" className="back-link">← Plans</Link>
          <h1>{plan.name}</h1>
          <p className="muted">
            <span className={`badge ${plan.kind === "custom" ? "badge-warn" : "badge-neutral"}`}>{plan.kind}</span>{" "}
            <span className="mono">{plan.id}</span> · {plan.orgCount} org{plan.orgCount === 1 ? "" : "s"}
          </p>
        </div>
        <div className="page-head-actions">
          {isStandard && (editing
            ? <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving}>Cancel</button>
            : <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>Edit</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={copy}>Copy</button>
          {plan.kind === "custom" && plan.orgCount === 0 && (
            <button className="btn btn-danger btn-sm" onClick={confirmDelete}>Delete</button>
          )}
        </div>
      </div>

      {isStandard && !editing && (
        <div className="notice-banner">
          This is a default plan. Editing is disabled to protect the catalog. Click <strong>Edit</strong> to change its
          values (chiefly the <strong>Dodo product ids</strong> below). Changes apply platform-wide. Or use <strong>Copy</strong> to
          spin up a separate custom plan instead.
        </div>
      )}
      {isStandard && editing && (
        <div className="notice-banner notice-warn">
          Editing a <strong>default plan</strong>. These values apply to every organization on it. Save carefully.
        </div>
      )}

      <div className="grid grid-2 detail-grid">
        <div className="card card-pad">
          <h3>Details</h3>
          <div className="field">
            <label className="form-label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
          </div>
          <div className="custom-grid">
            <div className="field">
              <label className="form-label">Monthly request limit</label>
              <input className="input" inputMode="numeric" value={monthly} onChange={(e) => setMonthly(e.target.value.replace(/[^\d]/g, ""))} placeholder="blank or 0 = unlimited" disabled={readOnly} />
            </div>
            <div className="field">
              <label className="form-label">Rate limit (requests / min)</label>
              <input className="input" inputMode="numeric" value={rate} onChange={(e) => setRate(e.target.value.replace(/[^\d]/g, ""))} placeholder="e.g. 600" disabled={readOnly} />
            </div>
          </div>
          {!readOnly && (
            <div className="field">
              <label className="check-row">
                <input type="checkbox" checked={historyApiEnabled} onChange={(e) => setHistoryApiEnabled(e.target.checked)} />
                History API (/history)
              </label>
              <p className="hint">Allow this plan to call the historical time-series endpoint. Growth+ by default.</p>
            </div>
          )}
          {!readOnly && plan.kind === "custom" && (
            <label className="check-row">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
              Archived (hidden from assignment)
            </label>
          )}
        </div>

        <div className="card card-pad">
          <h3>Pricing</h3>
          <p className="hint">Price per billing interval (USD). Leave blank to not offer that interval.</p>
          {suggested > 0 ? (
            <div className="suggest-box">
              <span>
                💡 Suggested ≈ <strong>${suggested}/mo</strong> for {monthlyReq.toLocaleString("en-US")} requests (a
                volume-based estimate, not applied automatically).
              </span>
              {!readOnly && (
                <button className="btn btn-ghost btn-sm" onClick={applySuggested}>Use suggested pricing</button>
              )}
            </div>
          ) : (
            <p className="hint muted">Set a monthly request limit above to get a price suggestion.</p>
          )}
          <div className="price-grid">
            {INTERVALS.map((i) => (
              <div className="field" key={i}>
                <label className="form-label">{intervalLabel(i as PlanInterval)}</label>
                <div className="price-input">
                  <span className="price-prefix">$</span>
                  <input className="input" inputMode="decimal" value={prices[i] ?? ""} onChange={(e) => setPrices((p) => ({ ...p, [i]: e.target.value.replace(/[^\d.]/g, "") }))} placeholder="—" disabled={readOnly} />
                </div>
              </div>
            ))}
          </div>
          <div className="field" style={{ marginTop: "12px" }}>
            <label className="form-label">Over-usage rate</label>
            <div className="overage-row">
              <span className="price-prefix">$</span>
              <input className="input" inputMode="decimal" value={overagePrice} onChange={(e) => setOveragePrice(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0.25" disabled={readOnly} />
              <span className="overage-per">per</span>
              <input className="input" inputMode="numeric" value={overageUnit} onChange={(e) => setOverageUnit(e.target.value.replace(/[^\d]/g, ""))} placeholder="10000" disabled={readOnly} />
              <span className="overage-per">requests</span>
            </div>
            <p className="hint">Usage beyond the monthly limit is billed at this rate instead of blocked. Leave blank to hard-cap.</p>
          </div>
        </div>
      </div>

      {/* Dodo Payments — map each priced interval to a Dodo product id */}
      <div className="card card-pad">
        <div className="section-row between">
          <h3>Billing (Dodo)</h3>
          <span className="badge badge-neutral">product ids</span>
        </div>
        <p className="hint">
          Each priced interval needs a Dodo product id to start checkout. Inert until Dodo is configured;
          leave blank for now and fill in before production.
        </p>
        {INTERVALS.filter((i) => (prices[i]?.trim() ?? "") !== "").length === 0 ? (
          <p className="muted">Set a price on an interval above to map its Dodo product.</p>
        ) : (
          <div className="dodo-map">
            {INTERVALS.filter((i) => (prices[i]?.trim() ?? "") !== "").map((i) => (
              <div className="dodo-row" key={i}>
                <span className="dodo-label">
                  {intervalLabel(i as PlanInterval)} <span className="muted">${prices[i]}</span>
                </span>
                <input
                  className="input mono"
                  value={dodoIds[i] ?? ""}
                  onChange={(e) => setDodoIds((d) => ({ ...d, [i]: e.target.value.trim() }))}
                  placeholder="pdt_…"
                  disabled={readOnly}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Organizations using / with access to this plan */}
      <div className="card card-pad">
        <div className="section-row between">
          <h3>Organizations</h3>
          <span className="muted">{plan.orgs.length}</span>
        </div>
        <p className="hint">
          Organizations this plan is available to. An org's <strong>active</strong> plan can't be removed here.
        </p>
        <AddOrgRow planId={plan.id} existingIds={plan.orgs.map((o) => o.id)} onUpdated={mergeOrgs} />
        {plan.orgs.length === 0 ? (
          <p className="muted">No organizations yet. Add one above.</p>
        ) : (
          <div className="member-list">
            {plan.orgs.map((o) => (
              <div className="member-row" key={o.id}>
                <Link to={`/organizations/${o.id}`} className="member-meta linkable">
                  <span className="name">{o.name}</span>
                  <span className="email mono muted">{o.slug}</span>
                </Link>
                {o.active ? (
                  <>
                    <span className="badge badge-ok">active</span>
                    <span className="role-locked" title="Active plan. Can't be removed">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="5" y="11" width="14" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      locked
                    </span>
                  </>
                ) : (
                  <button className="btn btn-ghost btn-sm" onClick={() => confirmRemove(o)}>Remove</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="page-foot">
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><span className="spinner" />Saving…</> : "Save plan"}
          </button>
        </div>
      )}
      {dialog}
    </>
  );
}

function AddOrgRow({
  planId,
  existingIds,
  onUpdated,
}: {
  planId: string;
  existingIds: string[];
  onUpdated: (d: PlanDetail) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<AdminOrgSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debQ = useDebounced(q, 250);

  useEffect(() => {
    let alive = true;
    if (!debQ.trim()) {
      setResults([]);
      return;
    }
    listOrgs({ q: debQ, pageSize: 8 })
      .then((r) => alive && setResults(r.items))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [debQ]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  async function add(orgId: string) {
    setBusy(true);
    try {
      onUpdated(await addPlanOrg(planId, orgId));
      setQ("");
      setResults([]);
      setOpen(false);
      toast("Organization added.", true);
    } catch (e) {
      toast(errMsg(e, "Could not add organization."));
    } finally {
      setBusy(false);
    }
  }

  const available = results.filter((o) => !existingIds.includes(o.id));

  return (
    <div className="add-org" ref={wrapRef}>
      <div className="search-box">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          className="search-input"
          value={q}
          disabled={busy}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search organizations to add…"
        />
      </div>
      {open && debQ.trim() !== "" && (
        <div className="add-org-menu">
          {available.length === 0 ? (
            <div className="add-org-empty muted">No matches</div>
          ) : (
            available.map((o) => (
              <button key={o.id} type="button" disabled={busy} onClick={() => add(o.id)}>
                <span className="name">{o.name}</span>
                <span className="mono muted">{o.slug}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
